<template>
  <div class="d-flex align-center flex-wrap">
    <div
      v-for="(tool, index) in modelValue"
      :key="'tool_' + index"
      class="ml-3 mb-4"
    >
      <div v-if="toolOptions().indexOf(tool.type) == -1">
        <v-tooltip location="bottom">
          <template v-slot:activator="{ props }">
            <v-chip
              v-bind="props"
              variant="flat"
              @click="editTool(index)"
              color="active-teal"
            >
              <span>{{ getToolName(tool.type) }}</span>
              <v-icon size="small" class="ml-1" color="dark-teal"
                >mdi-pencil-outline</v-icon
              >
            </v-chip>
          </template>

          <span>{{
            getToolDescriptionShort(tool.type, tool.primary, tool.secondary)
          }}</span>
        </v-tooltip>
      </div>

      <div v-else>Error, please reload.</div>
    </div>

    <v-dialog persistent v-model="showDialog" max-width="500">
      <v-card>
        <v-card-title class="text-dark-teal d-flex bg-active-teal">
          <div class="pr-4 d-flex align-center">
            <v-icon size="large" color="dark-teal">mdi-wrench</v-icon>
          </div>

          <div class="d-flex flex-column">
            <div class="text-h6"><span v-t="'tool'"></span></div>
            <div class="text-subtitle-1">{{ fieldName }}</div>
          </div>

          <v-spacer></v-spacer>

          <v-btn
            v-if="editIndex > -1"
            icon
            variant="text"
            v-on:click="deleteTool()"
          >
            <v-icon color="dark-teal">mdi-delete</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text style="font-size: 16px">
          <div class="text-dark-gray">
            <span v-t="'tool_message_1'"></span>
            <span v-t="'tool_message_4'"></span>
          </div>

          <v-select
            @update:modelValue="onToolChange()"
            :items="toolOptions()"
            v-model="type"
            :label="$t('tool')"
            color="primary"
            variant="underlined"
            class="mt-2"
          />

          <div v-if="type == 'Slice'">
            <v-text-field
              v-model="primary"
              :label="$t('starting_at')"
              type="number"
              color="primary"
              variant="underlined"
            />
            <v-text-field
              v-model="secondary"
              :label="$t('ends_at')"
              type="number"
              color="primary"
              variant="underlined"
            />
          </div>

          <div v-else-if="type == 'Split'">
            <v-text-field
              v-model="primary"
              :label="$t('divider')"
              color="primary"
              variant="underlined"
            />
            <v-select
              :items="toolSplitOptions()"
              v-model="secondary"
              :label="$t('keep_only')"
              color="primary"
              variant="underlined"
            />
          </div>

          <div v-else-if="type == 'Replace'">
            <v-text-field
              v-model="primary"
              :label="this.$t('search_content')"
              color="primary"
              variant="underlined"
            />
            <v-text-field
              v-model="secondary"
              :label="this.$t('replace_with')"
              color="primary"
              variant="underlined"
            />
          </div>

          <div v-else-if="type == 'Regex'" class="d-flex">
            <v-text-field
              v-model="primary"
              :label="this.$t('regex_pattern')"
              color="primary"
              variant="underlined"
            />

            <div class="d-flex align-center">
              <v-btn
                variant="text"
                icon
                @click.stop="
                  showRegexDialog = true;
                  regexReset();
                "
                ><v-icon color="primary">mdi-tune</v-icon></v-btn
              >
            </div>
          </div>

          <v-alert
            class="my-0"
            v-if="type.length"
            :value="true"
            variant="outlined"
            color="primary"
          >
            <v-icon color="orange" class="mr-1">mdi-information</v-icon>

            <span v-if="type == 'Slice'">
              <span v-t="'slice_info'"></span>
              <div>
                {{ this.$t("example") }}: text = "abcdefg",
                {{ $t("starting_at") }} = 1, {{ $t("ends_at") }} = -1,
                {{ this.$t("result") }} = "bcdef"
              </div>
            </span>

            <span v-else-if="type == 'Split'">
              <span v-t="'split_info'"></span>
              <div>
                {{ this.$t("example") }}: text = "a - b - c" ,
                {{ this.$t("divider") }} = '-', {{ $t("keep_only") }} = last ,
                {{ this.$t("result") }} = "c"
              </div>
            </span>

            <span v-else-if="type == 'Regex'">
              <span v-t="'regex_validate_info_1'" class="mr-1"></span>
              <span v-t="'with'" class="mr-1"></span>Regex<span
                v-t="'calculator'"
                class="ml-1"
              ></span>
              (<v-icon size="small">mdi-tune</v-icon>)
            </span>

            <span v-if="type == 'Lowercase'" v-t="'lowercase_rule_for_field'">
            </span>

            <span v-else-if="type == 'Replace'" v-t="'replace_info'"> </span>
          </v-alert>
        </v-card-text>

        <v-card-actions class="d-flex">
          <v-spacer />

          <v-btn
            height="30px"
            elevation="0"
            variant="outlined"
            color="primary"
            class="rounded-md"
            @click.stop="showDialog = false"
            ><span v-t="'cancel'"></span
          ></v-btn>

          <v-btn
            v-if="editIndex > -1"
            height="30px"
            elevation="0"
            color="primary"
            class="rounded-md"
            variant="flat"
            v-on:click="updateTool()"
            :disabled="disableSave"
            ><span v-t="'save'"></span
          ></v-btn>
          <v-btn
            v-else
            height="30px"
            elevation="0"
            color="primary"
            class="rounded-md"
            variant="flat"
            v-on:click="addTool()"
            :disabled="disableSave"
            ><span v-t="'save'"></span
          ></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog persistent v-model="showRegexDialog" max-width="500">
      <v-card>
        <v-card-title class="text-dark-teal bg-active-teal">
          <span class="mr-1">RegEx</span><span v-t="'calculator'"></span
        ></v-card-title>

        <v-card-text style="font-size: 16px">
          <div class="text-dark-gray mb-3">
            <div style="text-align: justify">
              <span v-t="'regex_validate_info_1'"></span>(<v-icon
                size="small"
                color="primary"
                >mdi-refresh</v-icon
              >)
            </div>
            <span
              v-t="'regex_validate_info_2'"
              style="text-align: justify"
            ></span>
          </div>

          <div class="d-flex align-center">
            <v-text-field
              v-model="regex_input"
              label="Test-text"
              variant="underlined"
            />
            <v-icon size="large">mdi-arrow-right</v-icon>
            <v-text-field
              v-model="regexSample"
              readonly
              single-line
              variant="underlined"
            />

            <div class="d-flex align-center">
              <TooltipButton
                :disabledAction="!Boolean(regex_input) || !Boolean(primary)"
                :loading="regexLoading"
                @action-handler="validateRegExAction(getRegexBody())"
                iconName="mdi-refresh"
                :tooltipMsg="$t('regex_refresh_tooltip')"
              />
            </div>
          </div>

          <v-text-field
            v-model="primary"
            label="Mönster"
            variant="underlined"
          />
          <!-- <v-checkbox
            color="primary lighten-1"
            v-model="regex_i"
            hide-details
            label="(I) Ignorera stora/små bokstäver"
          /> -->
          <v-checkbox
            class="small-check"
            color="primary"
            v-model="regex_m"
            hide-details
            :label="$t('multi_level_support')"
          />
          <v-checkbox
            class="small-check"
            color="primary"
            v-model="regex_s"
            hide-details
            :label="$t('s_regex_secondary_input')"
          />
          <v-checkbox
            class="small-check"
            color="primary"
            v-model="regex_x"
            hide-details
            :label="$t('x_regex_seconday_input')"
          />
        </v-card-text>

        <v-card-actions class="d-flex justify-end">
          <v-spacer />
          <v-btn
            height="30px"
            elevation="0"
            variant="outlined"
            color="primary"
            class="rounded-md"
            @click.stop="showRegexDialog = false"
            ><span v-t="'cancel'"></span>
          </v-btn>

          <v-btn
            height="30px"
            elevation="0"
            variant="flat"
            color="primary"
            class="rounded-md"
            @click.stop="showRegexDialog = false"
            ><span v-t="'save'"></span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.text-subtitle-1,
.text-h6 {
  font-family: "Readex Pro", sans-serif !important;
  font-weight: 400 !important;
}
</style>

<script>
import settingsAPI from "@/api/customerSettings";
import TooltipButton from "@/components/tooltipButton";

export default {
  name: "FilterToolButton",
  components: {
    TooltipButton,
  },
  props: {
    fieldName: {
      type: String,
      default: "",
    },
    isCondition: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: Array,
      default: function () {
        return [];
      },
    },
  },
  data() {
    return {
      editIndex: -1,
      showDialog: false,
      showRegexDialog: false,
      type: "",
      primary: "",
      secondary: "",
      regex_input: "",
      regex_output: "",
      regex_i: false,
      regex_m: false,
      regex_s: false,
      regex_x: false,

      regexSample: "",
      regexLoading: false,
      tools: ["Lowercase", "Regex", "Replace", "Slice", "Split"],
      toolValuesSplit: ["first", "second", "third", "fourth", "fifth", "last"],
    };
  },

  emits: ["update:modelValue"],

  computed: {
    disableSave: function () {
      if (this.type.length === 0) {
        return true;
      }
      if (this.type === "Slice") {
        return (
          (!this.primary || parseInt(this.primary) === 0) &&
          (!this.secondary || parseInt(this.secondary) === 0)
        );
      }
      if (this.type === "Split") {
        if (this.primary.length && this.secondary) {
          return false;
        } else {
          return true;
        }
      }
      if (this.type === "Regex") {
        if (this.primary.length) {
          return false;
        } else {
          return true;
        }
      }
      if (this.type === "Replace") {
        if (this.primary.length) {
          return false;
        } else {
          return true;
        }
      }
      return false;
    },
  },
  methods: {
    addTool() {
      if (this.type === "Lowercase") {
        this.$emit("update:modelValue", [
          ...this.modelValue,
          {
            type: this.type,
            primary: "",
            secondary: "",
          },
        ]);
      } else if (["Slice", "Split", "Replace"].includes(this.type)) {
        this.$emit("update:modelValue", [
          ...this.modelValue,
          {
            type: this.type,
            primary: this.primary,
            secondary: this.secondary,
          },
        ]);
      } else if (this.type === "Regex") {
        let flags = "";
        if (this.regex_i) {
          flags += "I";
        }
        if (this.regex_m) {
          flags += "M";
        }
        if (this.regex_s) {
          flags += "S";
        }
        if (this.regex_x) {
          flags += "X";
        }
        this.$emit("update:modelValue", [
          ...this.modelValue,
          {
            type: this.type,
            primary: this.primary,
            secondary: flags,
          },
        ]);
      }
      this.clear();
    },

    clear() {
      this.editIndex = -1;
      this.type = "";
      this.showDialog = false;
    },

    deleteTool() {
      const result = this.modelValue.filter(
        (t) => t != this.modelValue[this.editIndex],
      );
      this.$emit("update:modelValue", result);
      this.clear();
    },

    editTool(index) {
      this.editIndex = index;
      this.type = this.modelValue[index].type;
      this.primary = this.modelValue[index].primary;
      this.secondary = this.modelValue[index].secondary;
      if (this.type === "Regex") {
        this.regex_i = this.secondary.indexOf("I") !== -1;
        this.regex_m = this.secondary.indexOf("M") !== -1;
        this.regex_s = this.secondary.indexOf("S") !== -1;
        this.regex_x = this.secondary.indexOf("X") !== -1;
      }
      this.showDialog = true;
    },

    newTool() {
      this.clear();
      this.showDialog = true;
    },

    onToolChange() {
      this.primary = "";
      this.secondary = "";
      this.regex_i = false;
      this.regex_m = false;
      this.regex_s = false;
      this.regex_x = false;
    },

    getRegexBody() {
      return {
        primary: this.primary,
        secondary:
          (this.regex_i ? "I" : "") +
          (this.regex_m ? "M" : "") +
          (this.regex_s ? "S" : "") +
          (this.regex_x ? "X" : ""),

        sample: this.regex_input,
      };
    },

    updateTool() {
      let editedModelValue = this.modelValue;

      editedModelValue[this.editIndex].type = this.type;
      if (this.type === "Lowercase") {
        editedModelValue[this.editIndex].primary = "";
        editedModelValue[this.editIndex].secondary = "";
      } else if (
        this.type === "Split" ||
        this.type === "Slice" ||
        this.type === "Replace"
      ) {
        editedModelValue[this.editIndex].primary = this.primary;
        editedModelValue[this.editIndex].secondary = this.secondary;
      } else if (this.type === "Regex") {
        let flags = "";
        if (this.regex_i) {
          flags += "I";
        }
        if (this.regex_m) {
          flags += "M";
        }
        if (this.regex_s) {
          flags += "S";
        }
        if (this.regex_x) {
          flags += "X";
        }
        editedModelValue[this.editIndex].primary = this.primary;
        editedModelValue[this.editIndex].secondary = flags;
      }
      this.$emit("update:modelValue", editedModelValue);
      this.clear();
    },

    getToolName(toolId) {
      return (
        {
          Lowercase: "Lowercase",
          Regex: "RegEx",
          Replace: this.$t("search_replace"),
          Slice: this.$t("prune"),
          Split: this.$t("split"),
        }[toolId] || this.$t("error_occured")
      );
    },

    toolOptions() {
      let options = [];
      this.tools.forEach((v) =>
        options.push({ value: v, title: this.getToolName(v) }),
      );
      return options;
    },

    toolSplitOptions() {
      let options = [];
      this.toolValuesSplit.forEach((v) =>
        options.push({ value: v, title: this.getToolSplitName(v) }),
      );
      return options;
    },

    getToolDescriptionShort(toolId, primary, secondary) {
      if (toolId === "Lowercase") {
        return this.$t("lowercase_rule_for_field");
      } else if (toolId === "Regex") {
        if (secondary) {
          return (
            this.$t("regex_pattern") +
            ": " +
            primary +
            " " +
            this.$t("applied_with_flags") +
            ": " +
            secondary
          );
        } else {
          return (
            this.$t("regex_pattern") + ": " + primary + " " + this.$t("applied")
          );
        }
      } else if (toolId === "Replace") {
        return (
          this.$t("replaces_each_text") +
          " " +
          primary +
          " " +
          this.$t("with") +
          " " +
          '"' +
          secondary +
          '"'
        );
      } else if (toolId === "Slice") {
        if (primary && secondary) {
          return (
            this.$t("slice_tooltip") +
            " " +
            primary +
            " " +
            this.$t("and") +
            " " +
            secondary +
            " " +
            this.$t("used")
          );
        } else if (primary) {
          return (
            this.$t("slice_tool_tip_p_1") +
            " " +
            primary +
            " " +
            this.$t("slice_tool_tip_p_2")
          );
        } else if (secondary) {
          return (
            this.$t("slice_tool_tip_s_1") +
            " " +
            secondary +
            " " +
            this.$t("used")
          );
        } else {
          return this.$t("error_occured");
        }
      } else if (toolId === "Split") {
        return (
          this.$t("split_tooltip_1") +
          " " +
          primary +
          " " +
          this.$t("whereupon") +
          " " +
          this.getToolSplitName(secondary).toLowerCase() +
          " " +
          this.$t("split_tooltip_3")
        );
      } else {
        return this.$t("error_occured");
      }
    },

    async validateRegExAction(postBody) {
      try {
        this.regexLoading = true;
        let resp = await settingsAPI.validateRegEx("my_customer", postBody);

        this.regexSample = resp.validation;
      } catch (error) {
        console.log(error);
      } finally {
        this.regexLoading = false;
      }
    },

    regexReset() {
      this.regexLoading = false;
      this.regexSample = "";
    },

    getToolSplitName(toolValueId) {
      return (
        {
          first: this.$t("first"),
          second: this.$t("second"),
          third: this.$t("third"),
          fourth: this.$t("fourth"),
          fifth: this.$t("fifth"),
          last: this.$t("last"),
        }[toolValueId] || ""
      );
    },
  },
};
</script>
